// Display settings: projection, hemisphere, net grid, contours, density method and the
// counting circle, poles, gm / bm, set planes and cones, net / rosette view.
// GPL-3.0-or-later.
import type * as S from "@dipfield/stereo";
import { $ } from "../../lib/dom.js";
import { P, on, update, viewChanged, onRender } from "./ctx.js";
import { invalidateRaster } from "./net.js";
import { setMainView } from "./rose.js";

function sync() {
  const p = P(), s = p.stereo, d = s.display;
  const set = (name: string, v: string) => document.querySelectorAll<HTMLInputElement>(`input[name="${name}"]`).forEach((r) => (r.checked = r.value === v));
  set("method", s.projection.method); set("hemi", s.projection.hemisphere); set("dens", s.density.method);
  set("overlay", d.overlay); set("cstyle", d.contourStyle); set("mview", d.view);
  $<HTMLInputElement>("c-cc").value = String(+(s.density.countCircle * 100).toFixed(2));
  $<HTMLInputElement>("c-poles").checked = d.poles; $<HTMLInputElement>("c-contour").checked = d.contours;
  $<HTMLInputElement>("c-mean").checked = d.globalMean; $<HTMLInputElement>("c-best").checked = d.bestFit;
  $<HTMLInputElement>("c-setplanes").checked = d.setPlanes; $<HTMLInputElement>("c-cones").checked = d.cones;
  $<HTMLInputElement>("c-size").value = String(d.poleSize); $<HTMLInputElement>("c-rose").checked = s.rose.overlay;
}

export function mountDisplay() {
  onRender(sync);
  sync();
  const seg = (name: string, apply: (v: string) => void, recompute = true) =>
    document.querySelectorAll<HTMLInputElement>(`input[name="${name}"]`).forEach((r) => on(r, "change", () => {
      if (!r.checked) return; apply(r.value); if (recompute) update(false); else viewChanged();
    }));
  const s = () => P().stereo;
  seg("method", (v) => (s().projection = { ...s().projection, method: v as S.Method }));
  seg("hemi", (v) => (s().projection = { ...s().projection, hemisphere: v as S.Hemisphere }));
  seg("dens", (v) => (s().density.method = v === "schmidt" ? "schmidt" : "fisher-overlap"));
  seg("cstyle", (v) => { s().display.contourStyle = v === "smooth" ? "smooth" : "bands"; invalidateRaster(); }, false);
  seg("overlay", (v) => (s().display.overlay = v as S.OverlayKind), false);
  seg("mview", (v) => setMainView(v === "rose" ? "rose" : "net"), false);
  const chk = (id: string, k: "poles" | "contours" | "globalMean" | "bestFit" | "setPlanes" | "cones") =>
    on($(id), "change", (e: Event) => { s().display[k] = (e.target as HTMLInputElement).checked; viewChanged(); });
  chk("c-poles", "poles"); chk("c-contour", "contours"); chk("c-mean", "globalMean"); chk("c-best", "bestFit");
  chk("c-setplanes", "setPlanes"); chk("c-cones", "cones");
  on($("c-size"), "input", (e: Event) => { s().display.poleSize = +(e.target as HTMLInputElement).value; viewChanged(); });
  on($("c-cc"), "change", (e: Event) => {
    const el = e.target as HTMLInputElement, v = Number(el.value);
    if (!(v >= 0.1 && v <= 10)) { el.value = String(s().density.countCircle * 100); return; }
    s().density.countCircle = v / 100; update(false);
  });
}
